import { Component, type ErrorInfo, type ReactNode } from "react";
import { FEHLERTEXTE, istChunkLadefehler } from "../../lib/fehlertext.ts";

/**
 * Die ErrorBoundary der Wurzel (Fehleranzeige Paket 1, Spec
 * `docs/superpowers/specs/2026-10-19-fehleranzeige-fundament-design.md`, Abschnitt 3). Wirft eine
 * Komponente beim Rendern, baut React den ganzen Baum ab, und die Flaeche bleibt weiss. Die Grenze
 * faengt das ab und zeigt statt dessen eine schlichte Fehlerflaeche mit einem Knopf zum Neuladen.
 *
 * Eingehaengt wird sie in `src/App.tsx` um `<Speicher>`, NICHT um den `<FehlerStapel />`: der Stapel
 * bleibt bei einem Absturz sichtbar, und Meldungen, die dann noch eintreffen (etwa vom globalen
 * Fehlerfaenger), gehen nicht verloren.
 *
 * - Im Normalfall kommen die `children` ohne jeden Huellknoten zurueck. Die Visualtests vergleichen
 *   den ganzen `<body>`; ein zusaetzlicher Knoten braeche jede Ganzseiten-Signatur.
 * - Es gibt keinen Weg zurueck in die Anwendung ohne Neuladen: nach einem Renderabsturz ist ihr
 *   Zustand unbekannt, und ein erneutes Rendern desselben Zustands wirft meist wieder.
 * - `componentDidCatch` schreibt das Original in die Konsole (Spec). Der Nutzer sieht davon nichts;
 *   was ihm fehlt, ist der Hinweis, dass er neu laden kann.
 * - Die Flaeche ist bewusst ohne eigenes Stylesheet: sie erscheint nur im Absturzfall.
 * - Ein Chunk-Ladefehler (Ruling R-T3: ein alter Tab nach einem Deployment, der Seitenteil `lazy(() =>
 *   import(...))` liegt nicht mehr unter dem alten Namen) sagt dem Nutzer mit "Hier ist etwas
 *   schiefgelaufen." nichts; `FEHLERTEXTE.neuLaden` sagt, was zu tun ist. Ob es einer ist, entscheidet das reine Praedikat
 *   `istChunkLadefehler` des Uebersetzers (`lib/fehlertext.ts`); der Knopf bleibt derselbe.
 */
export const FEHLERGRENZE_TEXT = "Hier ist etwas schiefgelaufen.";

interface Eigenschaften {
  children: ReactNode;
}

interface Zustand {
  abgestuerzt: boolean;
  /** Der Absturz war ein Chunk-Ladefehler: die Flaeche nennt dann den Neuladen-Hinweis. */
  neuLaden: boolean;
}

export class FehlerGrenze extends Component<Eigenschaften, Zustand> {
  state: Zustand = { abgestuerzt: false, neuLaden: false };

  static getDerivedStateFromError(fehler: unknown): Zustand {
    // Nur das reine Praedikat: `fehlertext()` schriebe fuer einen Programmfehler mitten in der Renderphase ein
    // zusaetzliches `console.error`.
    return { abgestuerzt: true, neuLaden: istChunkLadefehler(fehler) };
  }

  componentDidCatch(fehler: unknown, info: ErrorInfo): void {
    console.error("Renderabsturz:", fehler, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.abgestuerzt) return this.props.children;
    return (
      <div role="alert">
        <p>{this.state.neuLaden ? FEHLERTEXTE.neuLaden : FEHLERGRENZE_TEXT}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Seite neu laden
        </button>
      </div>
    );
  }
}
